// MUI-free copy of Ui-components/mvp-development/MvpStatsSection.
import {memo} from 'react';
import type {CSSProperties} from 'react';
import Container from '@/components/atoms/Container';
import Icon from '@/components/atoms/Icon';
import type {IconName} from '@/components/atoms/Icon';
import styles from './MvpStatsSection.module.scss';

type StatIcon = 'star' | 'cube' | 'clock' | 'people' | 'trend' | 'globe';

interface MvpStat {
    /** A built-in icon key, or a path to an SVG in /public/images. */
    icon: string;
    value: string;
    label: string;
}

interface MvpStatsSectionProps {
    heading: string;
    subheading?: string;
    stats: MvpStat[];
}

const ICONS: Record<StatIcon, IconName> = {
    star: 'StarBorder',
    cube: 'ViewInArOutlined',
    clock: 'AccessTimeOutlined',
    people: 'PeopleAltOutlined',
    trend: 'TrendingUpOutlined',
    globe: 'PublicOutlined'
};

const StatCard = ({stat}: {stat: MvpStat}) => {
    // Anything that is not one of the built-in keys is a file: a local
    // path or, since the assets moved to the CDN, an absolute URL.
    const isImage = !(stat.icon in ICONS);
    // CDN URLs already carry their own percent-encoding; only a raw
    // local path with a space still needs encoding here.
    const iconUrl = stat.icon.includes(' ') ? encodeURI(stat.icon) : stat.icon;
    // Quoted and encoded: some icon filenames contain spaces, which silently break a bare
    // `url(...)` and leave the box painted as a solid square.
    const maskStyle: CSSProperties | undefined = isImage
        ? {maskImage: `url("${iconUrl}")`, WebkitMaskImage: `url("${iconUrl}")`}
        : undefined;
    return (
        <div className={styles.statCard}>
            <div className={`${styles.iconTile} mvp-stat-icon-tile${isImage ? ' mvp-stat-icon-tile--img' : ''}`}>
                {isImage ? (
                    // The SVG is a single-colour glyph, so it is drawn as a mask over a
                    // solid box: `<img>` would keep its baked-in white on hover.
                    <div className={`${styles.iconImg} mvp-stat-icon-img`} aria-hidden="true" style={maskStyle} />
                ) : (
                    <Icon name={ICONS[stat.icon as StatIcon]} className={`${styles.icon} mvp-stat-icon`} />
                )}
            </div>
            <p className={`font-headers mvp-stat-value ${styles.value}`}>{stat.value}</p>
            <p className={`font-headers ${styles.label}`}>{stat.label}</p>
        </div>
    );
};

const MvpStatsSection = ({heading, subheading, stats}: MvpStatsSectionProps) => {
    const statCount = stats?.length ?? 0;
    // Desktop shows every stat in one row, so the column count follows the number of stats.
    const listStyle = {'--mvp-stats-cols': Math.max(statCount, 1)} as CSSProperties;

    return (
        <div className={styles.section}>
            <Container>
                <h2 className={`font-headers ${styles.heading}${subheading ? '' : ` ${styles.headingOnly}`}`}>{heading}</h2>
                {subheading && <p className={`font-regular-text ${styles.subheading}`}>{subheading}</p>}

                <div className={styles.cardWrap}>
                    <div className={styles.viewport}>
                        <div className={styles.track}>
                            <div className={`${styles.list} ${styles.listMain}`} style={listStyle}>
                                {stats?.map((stat) => (
                                    <StatCard key={stat.label} stat={stat} />
                                ))}
                            </div>
                            {/* Second copy for the seamless phone loop only: decorative, so it is
                                hidden from assistive tech and never shown from `sm`. */}
                            <div className={`${styles.list} ${styles.listCopy} mvp-stats-copy`} aria-hidden="true">
                                {stats?.map((stat) => (
                                    <StatCard key={stat.label} stat={stat} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default memo(MvpStatsSection);
